import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shippingService, type EstimatableCarrierCode } from '@/services/shipping.service';
import { buildEstimatePayload, type BuildEstimateInput } from '../utils/buildEstimatePayload';

export interface CarrierFeeState {
  status: 'loading' | 'ok' | 'error' | 'timeout';
  fee?: number;
  message?: string;
}

const DEBOUNCE_MS = 500;

/**
 * Phí ước tính của MỌI hãng đã kết nối cho đơn đang dựng.
 *
 * Debounce ở đây, không ở component: thu ngân gõ cân nặng là mỗi ký tự một lần
 * đổi form, hỏi hãng theo từng ký tự vừa chậm vừa tốn lệnh gọi của hãng.
 *
 * `enabled` do CHÍNH payload quyết định (`null` = chưa đủ dữ liệu), nên điều
 * kiện đủ chỉ tồn tại ở một chỗ duy nhất là `buildEstimatePayload`.
 */
export function useCarrierFeeEstimates(input: BuildEstimateInput): {
  feeByCarrier: Map<string, CarrierFeeState>;
  isFetching: boolean;
  refetch: () => void;
} {
  const payload = buildEstimatePayload(input);
  const payloadKey = payload ? JSON.stringify(payload) : null;
  // Khởi tạo `null`, KHÔNG phải `payloadKey`: nếu để giá trị hợp lệ ngay từ
  // lần render đầu đi thẳng vào state, nó lách qua debounce 500ms — gọi hãng
  // ngay lập tức rồi gọi lại lần nữa khi debounce ổn định, phá vỡ đúng thứ
  // debounce ở đây tồn tại để đảm bảo (gộp nhiều lần đổi thành MỘT lần gọi).
  const [debouncedKey, setDebouncedKey] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKey(payloadKey), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [payloadKey]);

  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: ['shipping', 'estimate-fees', debouncedKey],
    queryFn: () =>
      shippingService.estimateFees(
        JSON.parse(debouncedKey as string) as ReturnType<typeof buildEstimatePayload> & object,
      ),
    enabled: debouncedKey !== null,
    retry: 0,
    staleTime: 60 * 1000,
  });

  const feeByCarrier = useMemo(() => {
    const map = new Map<string, CarrierFeeState>();
    const providers = input.providers as readonly EstimatableCarrierCode[];

    if (debouncedKey === null) return map;

    if (isError) {
      // Không có phản hồi nào ⇒ mọi hãng đều "không tính được". Bỏ trống sẽ để
      // các dòng kẹt mãi ở spinner.
      providers.forEach((p) => map.set(p, { status: 'error', message: 'Không tính được phí' }));
      return map;
    }

    if (!data) {
      providers.forEach((p) => map.set(p, { status: 'loading' }));
      return map;
    }

    data.estimates.forEach((e) =>
      map.set(e.provider, {
        status: e.status === 'ok' ? 'ok' : e.status,
        fee: e.fee,
        message: e.message,
      }),
    );
    // BE (Task 1) đáng lẽ trả đủ một estimate cho mỗi provider đã hỏi, nhưng
    // hook này không tin suông — hãng nào rơi mất khỏi response vẫn phải có
    // một entry rõ ràng, không để lại khoảng trống "không có trong map" mà
    // docstring ở trên đã nói là chính thứ hook này sinh ra để loại bỏ.
    providers.forEach((p) => {
      if (!map.has(p)) {
        map.set(p, { status: 'error', message: 'Hãng không trả về kết quả ước tính' });
      }
    });
    return map;
  }, [data, isError, debouncedKey, input.providers]);

  return {
    feeByCarrier,
    isFetching,
    // `refetch()` thủ công của TanStack Query chạy queryFn kể cả khi
    // `enabled: false` — nếu chưa đủ dữ liệu, debouncedKey là null và
    // queryFn sẽ JSON.parse('null') rồi gọi hãng với payload rỗng. Chặn ở
    // đây để refetch không bao giờ lách qua cổng "chưa đủ dữ liệu".
    refetch: () => {
      if (debouncedKey !== null) void refetch();
    },
  };
}
